import { Component, Inject, OnInit } from '@angular/core';
import { CustomTitleComponent } from '../../shared/components/custom-title/custom-title.component';
import { MaterialModule } from '@app/material.module';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OrdersService } from '../../services/entities/orders.service';
import { OrderDocument, SupplyOrder } from '@app/types';
import { Observable, of } from 'rxjs';
import { startWith, map, switchMap, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FileService, UploadResponse, UploadedFile } from '../../services/entities/file.service'; // Importar FileService (ruta relativa)

interface FilePreview {
    name: string;
    kind: 'image' | 'pdf' | 'other';
    /** URL local (objectURL) para archivos nuevos por seleccionar */
    localUrl?: string;
    /** URL remota ya subida (S3) */
    remoteUrl?: string;
    /** URL segura resuelta vía proxy (para ver/descargar) */
    secureUrl?: string;
    loading?: boolean;
}

@Component({
    selector: 'app-add-docs-dialog',
    imports: [CommonModule, CustomTitleComponent, MaterialModule, FormsModule, ReactiveFormsModule],
    templateUrl: './add-docs-dialog.component.html',
    styleUrls: ['./add-docs-dialog.component.scss'],
})
export class AddDocsDialogComponent implements OnInit {
    uiData: OrderDocument[] = [];
    ogOrder: SupplyOrder;
    newDocName: string = '';
    isLoading: boolean = false;

    /** Previews por doc: mainDoc -> FilePreview[] */
    previewsByDoc = new Map<string, FilePreview[]>();
    /** Lightbox activo (URL de imagen) */
    lightboxUrl: string | null = null;

    templateControl = new FormControl();
    allOrders: SupplyOrder[] = [];
    filteredOrders!: Observable<SupplyOrder[]>;

    constructor(
        private customSnackBar: CustomSnackBarService,
        public dialogRef: MatDialogRef<AddDocsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { order: SupplyOrder },
        private ordersService: OrdersService,
        private fileService: FileService, // Inyectar FileService
        private dialog: MatDialog
    ) {
        this.ogOrder = data.order;
    }

    ngOnInit() {
        if (this.ogOrder.docs && this.ogOrder.docs.length > 0) {
            this.uiData = JSON.parse(JSON.stringify(this.ogOrder.docs));
            // Asegurarse de que `files` sea un array vacío y `urls` exista
            this.uiData.forEach(doc => {
                doc.files = [];
                doc.urls = doc.urls || [];
                this.rebuildPreviews(doc);
            });
        }

        this.allOrders = this.ordersService.supplyOrdersSignal().filter((o) => o.docs && o.docs.length > 0 && o.id !== this.ogOrder.id);

        this.filteredOrders = this.templateControl.valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value)),
        );
    }

    // ---------- PREVIEWS ----------

    private fileKind(name: string): 'image' | 'pdf' | 'other' {
        const ext = name.toLowerCase().split('.').pop() || '';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
        if (ext === 'pdf') return 'pdf';
        return 'other';
    }

    private rebuildPreviews(doc: OrderDocument): void {
        const previews: FilePreview[] = [];

        // Archivos ya subidos (remotos)
        for (const url of doc.urls || []) {
            const name = url.split('/').pop() || url;
            previews.push({ name: this.cleanFileName(name), kind: this.fileKind(name), remoteUrl: url });
        }

        // Archivos locales nuevos
        for (const file of doc.files || []) {
            const kind = this.fileKind(file.name);
            const preview: FilePreview = { name: file.name, kind };
            if (kind === 'image') {
                preview.localUrl = URL.createObjectURL(file);
            }
            previews.push(preview);
        }

        this.previewsByDoc.set(doc.mainDoc, previews);
    }

    /** Los nombres se guardan como uuid_name.ext: mostrar solo la parte legible */
    private cleanFileName(name: string): string {
        const noQuery = name.split('?')[0];
        const parts = noQuery.split('_');
        return parts.length > 1 ? parts.slice(1).join('_') : noQuery;
    }

    getPreviews(doc: OrderDocument): FilePreview[] {
        return this.previewsByDoc.get(doc.mainDoc) || [];
    }

    getFileIcon(preview: FilePreview): string {
        if (preview.kind === 'image') return 'image';
        if (preview.kind === 'pdf') return 'picture_as_pdf';
        return 'description';
    }

    /** Abre el lightbox si es imagen; si no, marca la URL segura para descarga */
    openPreview(preview: FilePreview): void {
        if (preview.kind === 'image' && (preview.localUrl || preview.secureUrl)) {
            this.lightboxUrl = preview.localUrl || preview.secureUrl || null;
            return;
        }

        if (preview.remoteUrl && !preview.secureUrl) {
            preview.loading = true;
            const s3Key = preview.remoteUrl.split('amazonaws.com/')[1]?.split('?')[0];
            if (!s3Key) {
                preview.loading = false;
                this.openInNewTab(preview.remoteUrl);
                return;
            }
            this.fileService.getSecureFileUrl(s3Key).subscribe({
                next: (url) => {
                    preview.loading = false;
                    preview.secureUrl = url;
                    this.openInNewTab(url);
                },
                error: () => {
                    preview.loading = false;
                    this.openInNewTab(preview.remoteUrl!);
                }
            });
        } else if (preview.secureUrl) {
            this.openInNewTab(preview.secureUrl);
        }
    }

    private openInNewTab(url: string): void {
        window.open(url, '_blank');
    }

    closeLightbox(): void {
        this.lightboxUrl = null;
    }

    /** Elimina un archivo nuevo (aún no subido) por nombre */
    removeLocalFile(doc: OrderDocument, preview: FilePreview): void {
        const idx = (doc.files || []).findIndex(f => f.name === preview.name);
        if (idx >= 0) this.removeFile(doc, idx);
    }

    isExistingFile(preview: FilePreview): boolean {
        return !!preview.remoteUrl;
    }

    /** Elimina un archivo ya subido de doc.urls */
    removeExistingFile(doc: OrderDocument, preview: FilePreview): void {
        if (!preview.remoteUrl) return;
        doc.urls = (doc.urls || []).filter(u => u !== preview.remoteUrl);
        doc.uploaded = (doc.urls?.length || 0) + (doc.files?.length || 0) > 0;
        this.rebuildPreviews(doc);
        this.customSnackBar.open('Archivo removido (se aplicará al guardar).', 'info');
    }

    private _filter(value: string | SupplyOrder): SupplyOrder[] {
        const filterValue = (typeof value === 'string' ? value : value.folioOrden).toLowerCase();
        return this.allOrders.filter((order) =>
            order.folioOrden.toLowerCase().includes(filterValue) ||
            order.clienteRFC.toLowerCase().includes(filterValue) ||
            (order.factura?.receptorNombre || '').toLowerCase().includes(filterValue)
        );
    }

    displayFn(order: SupplyOrder): string {
        if (!order) { return ''; }
        const clientIdentifier = order.factura?.receptorNombre || order.clienteRFC;
        return `${order.folioOrden} - ${clientIdentifier}`;
    }

    onTemplateSelected(event: any): void {
        const selectedOrder = event.option.value as SupplyOrder;
        if (selectedOrder && selectedOrder.docs) {
            this.uiData = selectedOrder.docs.map((doc: OrderDocument) => ({
                ...doc,
                uploaded: false,
                validated: false,
                files: [],
                urls: [], // Resetear URLs al copiar plantilla
            }));
            this.customSnackBar.open(`Plantilla de la orden ${selectedOrder.folioOrden} copiada.`, 'success');
            this.templateControl.setValue('');
        }
    }

    addNewDocument(): void {
        if (this.newDocName && this.newDocName.trim() !== '') {
            const docExists = this.uiData.some((doc) => doc.mainDoc.toLowerCase() === this.newDocName.trim().toLowerCase());
            if (docExists) {
                this.customSnackBar.open('El tipo de documento ya existe.', 'warning');
                return;
            }

            this.uiData.push({
                mainDoc: this.newDocName.trim(),
                uploaded: false,
                validated: false,
                files: [],
                urls: [],
            });
            this.newDocName = '';
        }
    }

    removeDocument(index: number): void {
        this.uiData.splice(index, 1);
        this.customSnackBar.open('Tipo de documento eliminado.', 'info');
    }

    onFileSelected(event: Event, doc: OrderDocument): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            doc.files = Array.from(input.files); // Reemplazar en lugar de añadir
            doc.uploaded = doc.files.length > 0;
            this.rebuildPreviews(doc);
        }
        input.value = '';
    }

    removeFile(doc: OrderDocument, fileIndex: number): void {
        if (doc.files && doc.files.length > fileIndex) {
            const removed = doc.files[fileIndex];
            // Liberar objectURL si existe
            const preview = this.getPreviews(doc).find(p => p.name === removed.name && p.localUrl);
            if (preview?.localUrl) URL.revokeObjectURL(preview.localUrl);
            doc.files.splice(fileIndex, 1);
            doc.uploaded = doc.files.length > 0;
            this.rebuildPreviews(doc);
        }
    }

    _save(): void {
        this.isLoading = true;
        const pathPrefix = `orders/${this.ogOrder.folioOrden}`;

        // 1. Recolectar todos los archivos a subir
        const filesToUpload: File[] = this.uiData.flatMap(doc => doc.files);

        // 2. Determinar el flujo: con o sin subida de archivos
        const uploadObservable = filesToUpload.length > 0
            ? this.fileService.uploadFiles(filesToUpload, pathPrefix, 'ORDER')
            : of({ successful: [], failed: [] });

        uploadObservable.pipe(
            switchMap((uploadResult: UploadResponse) => {
                if (uploadResult.failed.length > 0) {
                    const failedNames = uploadResult.failed.map((f: { original_name: string; error: string; }) => f.original_name).join(', ');
                    this.customSnackBar.open(`Error al subir: ${failedNames}`, 'error');
                    throw new Error('File upload failed');
                }

                // 3. Mapear archivos subidos de vuelta a sus documentos
                const finalDocs: OrderDocument[] = this.uiData.map((doc: OrderDocument) => {
                    const newUrls = uploadResult.successful
                        .filter((file: UploadedFile) => doc.files.some((f: File) => f.name === file.original_name))
                        .map((file: UploadedFile) => file.url);

                    return {
                        mainDoc: doc.mainDoc,
                        subDoc: doc.subDoc,
                        uploaded: ((doc.urls?.length || 0) + newUrls.length) > 0,
                        validated: doc.validated,
                        urls: [...(doc.urls || []), ...newUrls],
                        files: [] // normalizar a arreglo vacío para cumplir con OrderDocument
                    } as OrderDocument;
                });

                // 4. Llamar al servicio para actualizar la orden
                return this.ordersService.updateOrderDocuments(this.ogOrder.id, finalDocs);
            }),
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: () => {
                this.customSnackBar.open('Documentos guardados correctamente.', 'success');
                this.dialogRef.close(true); // Enviar señal de éxito
            },
            error: (err: unknown) => {
                // El error de subida ya se notificó, aquí manejamos errores de la actualización de la orden
                if ((err as any)?.message !== 'File upload failed') {
                    this.customSnackBar.open('Error al actualizar la orden.', 'error');
                }
            }
        });
    }

    _close(): void {
        this.dialogRef.close(null);
    }
}
